import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  IInitiatePaymentResult,
  IPaymentIntentMethodAvailabilityResult,
  IPaymentStatus,
  PaymentIntentMethod,
} from '@kinet-open-network/shared/data-access/model';
import { plainToInstance } from 'class-transformer';
import { firstValueFrom, map } from 'rxjs';
import { PaymentStatus } from '../models/payment-status.model';

@Injectable({
  providedIn: 'root',
})
export class ZonePaymentsService {
  readonly #http = inject(HttpClient);

  async initiatePayment(
    method: PaymentIntentMethod,
    amount?: number,
    gateway?: string,
  ): Promise<IInitiatePaymentResult> {
    return firstValueFrom(
      this.#http.post<IInitiatePaymentResult>('/payments/initiate', {
        method,
        gateway,
        amount,
      }),
    );
  }

  async getAvailablePaymentMethods(): Promise<IPaymentIntentMethodAvailabilityResult> {
    return firstValueFrom(
      this.#http.get<IPaymentIntentMethodAvailabilityResult>(
        '/payments/methods',
      ),
    );
  }

  /**
   * Asks the payment provider for the current status of a payment attempt
   */
  async refreshPaymentStatus(intentId: string) {
    return plainToInstance(
      PaymentStatus,
      await firstValueFrom(
        this.#http.post<IPaymentStatus>(
          `/payments/status/${intentId}/refresh`,
          {},
        ),
      ),
    );
  }

  /**
   * Status of a payment attempt, e.g. the one the customer just came back to
   */
  async getPaymentStatusById(intentId: string) {
    return plainToInstance(
      PaymentStatus,
      await firstValueFrom(
        this.#http.get<IPaymentStatus>(`/payments/status/${intentId}`),
      ),
    );
  }

  getPaymentStatus() {
    return this.#http
      .get<{ data: IPaymentStatus | null }>('/payments/status')
      .pipe(
        map((res) => {
          if (res.data) {
            return plainToInstance(PaymentStatus, res.data);
          }

          return null;
        }),
      );
  }
}
